import CryptoJS from 'crypto-js';
export class CryptoService {
  private static readonly KEY = '12345678901234567890123456789012'; // 32 ký tự
  private static readonly IV = '1234567890123456'; // 16 ký tự

  /**
   * Mã hóa chuỗi văn bản bằng AES
   * @param plainText - Chuỗi cần mã hóa
   * @returns Chuỗi đã được mã hóa và encode base64
   */
  public static encryptAES(plainText: string): string {
    try {
      const key = CryptoJS.enc.Utf8.parse(this.KEY);
      const iv = CryptoJS.enc.Utf8.parse(this.IV);

      const encrypted = CryptoJS.AES.encrypt(plainText, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      return encrypted.toString();
    } catch (error) {
      console.error('❌ Encryption failed:', error);
      throw new Error('Encryption failed');
    }
  }

  /**
   * Giải mã chuỗi đã được mã hóa AES
   * @param cipherText - Chuỗi đã được mã hóa (base64)
   * @returns Chuỗi văn bản gốc
   */
  public static decryptAES(cipherText: string): string {
    try {
      const key = CryptoJS.enc.Utf8.parse(this.KEY);
      const iv = CryptoJS.enc.Utf8.parse(this.IV);

      const decrypted = CryptoJS.AES.decrypt(cipherText, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      const plainText = decrypted.toString(CryptoJS.enc.Utf8);

      if (!plainText) {
        // throw new Error('Decryption resulted in empty string');
      }

      return plainText;
    } catch (error) {
      console.error('❌ Decryption failed:', error);
      throw new Error('Decryption failed');
    }
  }

  /**
   * Kiểm tra xem chuỗi có phải là base64 hợp lệ không
   * @param str - Chuỗi cần kiểm tra
   * @returns true nếu là base64 hợp lệ
   */
  public static isValidBase64(str: string): boolean {
    try {
      return btoa(atob(str)) === str;
    } catch (err) {
      return false;
    }
  }

  /**
   * Mã hóa với key và IV tùy chỉnh
   * @param plainText - Chuỗi cần mã hóa
   * @param customKey - Key tùy chỉnh (32 ký tự)
   * @param customIV - IV tùy chỉnh (16 ký tự)
   * @returns Chuỗi đã được mã hóa
   */
  public static encryptWithCustomKey(plainText: string, customKey: string, customIV: string): string {
    if (customKey.length !== 32) {
      throw new Error('Key must be exactly 32 characters');
    }
    if (customIV.length !== 16) {
      throw new Error('IV must be exactly 16 characters');
    }

    try {
      const key = CryptoJS.enc.Utf8.parse(customKey);
      const iv = CryptoJS.enc.Utf8.parse(customIV);

      const encrypted = CryptoJS.AES.encrypt(plainText, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      return encrypted.toString();
    } catch (error) {
      console.error('❌ Custom encryption failed:', error);
      throw new Error('Custom encryption failed');
    }
  }

  /**
   * Giải mã với key và IV tùy chỉnh
   * @param cipherText - Chuỗi đã được mã hóa
   * @param customKey - Key tùy chỉnh (32 ký tự)
   * @param customIV - IV tùy chỉnh (16 ký tự)
   * @returns Chuỗi văn bản gốc
   */
  public static decryptWithCustomKey(cipherText: string, customKey: string, customIV: string): string {
    if (customKey.length !== 32) {
      throw new Error('Key must be exactly 32 characters');
    }
    if (customIV.length !== 16) {
      throw new Error('IV must be exactly 16 characters');
    }

    try {
      const key = CryptoJS.enc.Utf8.parse(customKey);
      const iv = CryptoJS.enc.Utf8.parse(customIV);

      const decrypted = CryptoJS.AES.decrypt(cipherText, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      const plainText = decrypted.toString(CryptoJS.enc.Utf8);

      if (!plainText) {
        // throw new Error('Custom decryption resulted in empty string');
      }

      return plainText;
    } catch (error) {
      console.error('❌ Custom decryption failed:', error);
      throw new Error('Custom decryption failed');
    }
  }

  /**
   * Generate random key (32 characters)
   * @returns Random 32-character string
   */
  public static generateRandomKey(): string {
    return CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Hex).substring(0, 32);
  }

  /**
   * Generate random IV (16 characters)
   * @returns Random 16-character string
   */
  public static generateRandomIV(): string {
    return CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex).substring(0, 16);
  }
}

// Export default instance for convenience
export default CryptoService;
